import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { API_URL } from '../config';
import './CourseManagement.css';

function CourseManagement() {
  const { user } = useUser();
  const [courses, setCourses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form states
  const [showCreateCourse, setShowCreateCourse] = useState(false);
  const [showCreateClass, setShowCreateClass] = useState(false);
  const [showJoinClass, setShowJoinClass] = useState(false);
  
  const [courseForm, setCourseForm] = useState({
    course_code: '',
    course_name: '',
    description: ''
  });
  
  const [classForm, setClassForm] = useState({
    course_id: '',
    class_name: '',
    semester: ''
  });
  
  const [joinCode, setJoinCode] = useState('');
  
  // Add student management states
  const [showStudentList, setShowStudentList] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load classes for all users
      const classesResponse = await fetch(`${API_URL}/api/classes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (classesResponse.ok) {
        const classesData = await classesResponse.json();
        setClasses(classesData);
      }

      // Load courses only for instructors
      if (user.role === 'instructor') {
        const coursesResponse = await fetch(`${API_URL}/api/courses`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        
        if (coursesResponse.ok) {
          const coursesData = await coursesResponse.json();
          setCourses(coursesData);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/courses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(courseForm),
      });

      if (response.ok) {
        setCourseForm({ course_code: '', course_name: '', description: '' });
        setShowCreateCourse(false);
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create course');
      }
    } catch (error) {
      console.error('Error creating course:', error);
      setError('Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/classes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(classForm),
      });

      if (response.ok) {
        setClassForm({ course_id: '', class_name: '', semester: '' });
        setShowCreateClass(false);
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create class');
      }
    } catch (error) {
      console.error('Error creating class:', error);
      setError('Failed to create class');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClass = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/classes/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ join_code: joinCode }),
      });

      if (response.ok) {
        setJoinCode('');
        setShowJoinClass(false);
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to join class');
      }
    } catch (error) {
      console.error('Error joining class:', error);
      setError('Failed to join class');
    } finally {
      setLoading(false);
    }
  };

  const loadClassStudents = async (classId) => {
    try {
      const response = await fetch(`${API_URL}/api/classes/${classId}/students`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const studentsData = await response.json();
        setClassStudents(studentsData);
      } else {
        setError('Failed to load class students');
      }
    } catch (error) {
      console.error('Error loading class students:', error);
      setError('Failed to load class students');
    }
  };

  const handleViewStudents = (classItem) => {
    setSelectedClass(classItem);
    setShowStudentList(true);
    loadClassStudents(classItem.id);
  };

  const handleRemoveStudent = async (studentNetId) => {
    if (!selectedClass) return;
    
    if (!window.confirm('Are you sure you want to remove this student from the class?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/classes/${selectedClass.id}/students/${studentNetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        // Reload student list
        loadClassStudents(selectedClass.id);
        // Reload classes to update student count
        loadData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to remove student');
      }
    } catch (error) {
      console.error('Error removing student:', error);
      setError('Failed to remove student');
    }
  };

  if (loading && classes.length === 0) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="course-management">
      <div className="course-header">
        <h1>
          {user.role === 'instructor' ? 'Course Management' : 'My Classes'}
        </h1>
        <div className="header-actions">
          {user.role === 'instructor' ? (
            <>
              <button 
                className="btn btn-primary"
                onClick={() => setShowCreateCourse(true)}
              >
                Create Course
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowCreateClass(true)}
              >
                Create Class
              </button>
            </>
          ) : (
            <button 
              className="btn btn-primary"
              onClick={() => setShowJoinClass(true)}
            >
              Join Class
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Classes List */}
      <div className="classes-section">
        <h2>
          {user.role === 'instructor' ? 'My Classes' : 'Enrolled Classes'}
        </h2>
        
        {classes.length === 0 ? (
          <div className="empty-state">
            <p>
              {user.role === 'instructor' 
                ? 'No classes created yet. Create a course first, then add classes to it.'
                : 'No classes joined yet. Use a join code from your instructor to join a class.'
              }
            </p>
          </div>
        ) : (
          <div className="classes-grid">
            {classes.map(classItem => (
              <div key={classItem.id} className="class-card">
                <div className="class-header">
                  <h3>{classItem.course?.course_code} - {classItem.class_name}</h3>
                  <span className="semester">{classItem.semester}</span>
                </div>
                <div className="class-details">
                  <p className="course-name">{classItem.course?.course_name}</p>
                  <div className="class-meta">
                    <span className="student-count">
                      👥 {classItem.student_count} students
                    </span>
                    {user.role === 'instructor' && (
                      <span className="join-code">
                        Code: <strong>{classItem.join_code}</strong>
                      </span>
                    )}
                  </div>
                  {user.role === 'instructor' && (
                    <div className="class-actions">
                      <button 
                        className="btn btn-small btn-outline"
                        onClick={() => handleViewStudents(classItem)}
                      >
                        👥 View Students
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Courses List (Instructors Only) - Only show if instructor has classes */}
      {user.role === 'instructor' && classes.length > 0 && (
        <div className="courses-section">
          <h2>My Courses</h2>
          
          {courses.length === 0 ? (
            <div className="empty-state">
              <p>No courses created yet. Create your first course to get started.</p>
            </div>
          ) : (
            <div className="courses-list">
              {courses.map(course => (
                <div key={course.id} className="course-item">
                  <div className="course-info">
                    <h4>{course.course_code} - {course.course_name}</h4>
                    {course.description && <p>{course.description}</p>}
                  </div>
                  <div className="course-actions">
                    <span className="created-date">
                      Created: {new Date(course.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Course Modal */}
      {showCreateCourse && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create New Course</h3>
              <button onClick={() => setShowCreateCourse(false)}>×</button>
            </div>
            <form onSubmit={handleCreateCourse}>
              <div className="form-group">
                <label>Course Code</label>
                <input
                  type="text"
                  value={courseForm.course_code}
                  onChange={(e) => setCourseForm({...courseForm, course_code: e.target.value})}
                  placeholder="e.g., CS411"
                  required
                />
              </div>
              <div className="form-group">
                <label>Course Name</label>
                <input
                  type="text"
                  value={courseForm.course_name}
                  onChange={(e) => setCourseForm({...courseForm, course_name: e.target.value})}
                  placeholder="e.g., Database Systems"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  value={courseForm.description}
                  onChange={(e) => setCourseForm({...courseForm, description: e.target.value})}
                  placeholder="Course description..."
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateCourse(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Class Modal */}
      {showCreateClass && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create New Class</h3>
              <button onClick={() => setShowCreateClass(false)}>×</button>
            </div>
            <form onSubmit={handleCreateClass}>
              <div className="form-group">
                <label>Course</label>
                <select
                  value={classForm.course_id}
                  onChange={(e) => setClassForm({...classForm, course_id: e.target.value})}
                  required
                >
                  <option value="">Select a course</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.course_code} - {course.course_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Class Name</label>
                <input
                  type="text"
                  value={classForm.class_name}
                  onChange={(e) => setClassForm({...classForm, class_name: e.target.value})}
                  placeholder="e.g., Section A, Morning Class"
                  required
                />
              </div>
              <div className="form-group">
                <label>Semester</label>
                <input
                  type="text"
                  value={classForm.semester}
                  onChange={(e) => setClassForm({...classForm, semester: e.target.value})}
                  placeholder="e.g., Fall 2024"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateClass(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Class'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Join Class Modal */}
      {showJoinClass && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Join Class</h3>
              <button onClick={() => setShowJoinClass(false)}>×</button>
            </div>
            <form onSubmit={handleJoinClass}>
              <div className="form-group">
                <label>Join Code</label>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="Enter 6-character code"
                  maxLength="6"
                  required
                />
                <small>Ask your instructor for the class join code</small>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowJoinClass(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Joining...' : 'Join Class'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Student List Modal */}
      {showStudentList && selectedClass && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <div className="modal-header">
              <h3>
                Students in {selectedClass.course?.course_code} - {selectedClass.class_name}
              </h3>
              <button onClick={() => setShowStudentList(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="student-list-header">
                <div className="student-count-info">
                  <span className="total-students">
                    {classStudents.length} students enrolled
                  </span>
                  <span className="join-code-display">
                    Join Code: <strong>{selectedClass.join_code}</strong>
                  </span>
                </div>
              </div>
              
              {classStudents.length === 0 ? (
                <div className="empty-state">
                  <p>No students have joined this class yet.</p>
                  <p>Share the join code <strong>{selectedClass.join_code}</strong> with your students.</p>
                </div>
              ) : (
                <div className="students-table">
                  <div className="table-header">
                    <div className="col-name">Name</div>
                    <div className="col-email">Email</div>
                    <div className="col-netid">NetID</div>
                    <div className="col-joined">Joined</div>
                    <div className="col-actions">Actions</div>
                  </div>
                  {classStudents.map(student => (
                    <div key={student.id} className="table-row">
                      <div className="col-name">
                        <div className="student-info">
                          <span className="student-name">{student.student_name}</span>
                        </div>
                      </div>
                      <div className="col-email">{student.student_email}</div>
                      <div className="col-netid">{student.student_net_id}</div>
                      <div className="col-joined">
                        {new Date(student.joined_at).toLocaleDateString()}
                      </div>
                      <div className="col-actions">
                        <button
                          className="btn-remove"
                          onClick={() => handleRemoveStudent(student.student_net_id)}
                          title="Remove student from class"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowStudentList(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseManagement;
